import { TAXONOMY_DISPLAY } from '@archetelos/lib/taxonomy';
import { queryOptions } from '@tanstack/react-query';

import type { FeedRow } from '@/lib/queries/subscriber';

import { contentHref } from '@/lib/contentHref';
import { continueWatchingCutoffIso } from '@/lib/queries/browse';
import { supabase } from '@/lib/supabase';

// Member-home composition (Spec 18). Zero schema: every row recomposes queries
// that already exist. This module adds the two home-specific reads (latest
// in-progress read; new-for-you candidates), the shared subscriber_preferences
// read/write, and the pure `buildNewForYou` cascade (unit-tested).

/** One in-progress item for the unified "Continue" row — article or video. */
export interface ContinueItem {
  contentId: string;
  kind: 'article' | 'video';
  title: string;
  slug: string;
  href: string; // via the contentHref(type, slug) seam
  coverImageUrl: string | null;
  coverImageAlt: string | null;
  percent: number; // 0–100 (scroll_percent for articles; position/duration for videos)
  lastActivityAt: string; // last_read_at | last_watched_at
}

/** Decoupled inputs for buildContinueItems (no supabase row shapes). */
export interface ContinueArticleInput {
  contentId: string;
  title: string;
  slug: string;
  coverImageUrl: string | null;
  coverImageAlt: string | null;
  scrollPercent: number;
  lastReadAt: string;
}
export interface ContinueVideoInput {
  contentId: string;
  title: string;
  slug: string;
  coverImageUrl: string | null;
  coverImageAlt: string | null;
  positionSeconds: number;
  durationSeconds: number | null;
  lastWatchedAt: string;
}

/** Latest-published candidate for the New-for-you cascade (steps 2 + 3). */
export interface HomeCandidate {
  contentId: string;
  type: string;
  title: string;
  slug: string;
  traditionTags: string[];
  isEditorsPick: boolean;
  publishedAt: string | null;
  creatorName: string | null;
  creatorAvatarUrl: string | null;
}

export interface NewForYouItem {
  contentId: string;
  href: string;
  title: string;
  /** Warm attribution line; null renders a plain (un-attributed) latest item. */
  attribution: string | null;
  isVideo: boolean;
  creatorName: string | null;
  creatorAvatarUrl: string | null;
}

/**
 * The New-for-you cascade (pure, unit-tested). Exactly `limit` items, deduped:
 *   1. newest from followed creators   → "New from {Creator} · following"
 *   2. tradition-matched fill          → "In the {tradition} tradition"
 *   3. editors' picks then plain latest → "From the editors" / no attribution
 * Steps read their sources in the order given; each skips ids already placed.
 * `followed` and `candidates` are assumed newest-first (their queries order so).
 */
export function buildNewForYou(args: {
  followed: FeedRow[];
  candidates: HomeCandidate[];
  preferenceTags: string[];
  limit?: number;
  displayTag?: (tag: string) => string;
}): NewForYouItem[] {
  const { followed, candidates, preferenceTags } = args;
  const limit = args.limit ?? 3;
  const displayTag = args.displayTag ?? ((t: string) => TAXONOMY_DISPLAY[t] ?? t);

  const out: NewForYouItem[] = [];
  const seen = new Set<string>();
  const push = (item: NewForYouItem) => {
    if (out.length >= limit || seen.has(item.contentId)) return;
    seen.add(item.contentId);
    out.push(item);
  };

  // 1. Followed creators, newest first.
  for (const f of followed) {
    if (out.length >= limit) break;
    const name = f.creator_display_name ?? f.creator_username ?? 'a contributor you follow';
    push({
      contentId: f.content_id,
      href: contentHref(f.type, f.slug),
      title: f.title,
      attribution: `New from ${name} · following`,
      isVideo: f.type === 'video',
      creatorName: f.creator_display_name ?? f.creator_username ?? null,
      creatorAvatarUrl: null,
    });
  }

  // 2. Tradition-matched fill against the member's onboarding preferences.
  const prefs = new Set(preferenceTags);
  for (const c of candidates) {
    if (out.length >= limit) break;
    const matched = c.traditionTags.find((t) => prefs.has(t));
    if (!matched) continue;
    push({
      contentId: c.contentId,
      href: contentHref(c.type, c.slug),
      title: c.title,
      attribution: `In the ${displayTag(matched)} tradition`,
      isVideo: c.type === 'video',
      creatorName: c.creatorName,
      creatorAvatarUrl: c.creatorAvatarUrl,
    });
  }

  // 3. Editors' picks first, then plain latest.
  const byEditors = [...candidates].sort((a, b) => {
    if (a.isEditorsPick !== b.isEditorsPick) return a.isEditorsPick ? -1 : 1;
    return (b.publishedAt ?? '').localeCompare(a.publishedAt ?? '');
  });
  for (const c of byEditors) {
    if (out.length >= limit) break;
    push({
      contentId: c.contentId,
      href: contentHref(c.type, c.slug),
      title: c.title,
      attribution: c.isEditorsPick ? 'From the editors' : null,
      isVideo: c.type === 'video',
      creatorName: c.creatorName,
      creatorAvatarUrl: c.creatorAvatarUrl,
    });
  }

  return out;
}

// The unified resume band: below 5% you barely started, at/above 95% you
// effectively finished. Articles are pre-banded in SQL (scroll_percent is a
// stored column); videos band here on computed percent, since PostgREST can't
// filter the position/duration ratio.
const CONTINUE_BAND_MIN = 5;
const CONTINUE_BAND_MAX = 95;

/**
 * Merge in-progress articles and videos into one recency-ordered resume list
 * (pure, unit-tested like buildNewForYou). Applies the 5–95% band to both,
 * excludes videos with no duration (no meaningful resume signal — a bar at 0%),
 * dedupes by contentId, and caps at `limit`.
 */
export function buildContinueItems(args: {
  articles: ContinueArticleInput[];
  videos: ContinueVideoInput[];
  limit?: number;
}): ContinueItem[] {
  const limit = args.limit ?? 4;
  const merged: ContinueItem[] = [];

  for (const a of args.articles) {
    if (a.scrollPercent < CONTINUE_BAND_MIN || a.scrollPercent >= CONTINUE_BAND_MAX) continue;
    merged.push({
      contentId: a.contentId,
      kind: 'article',
      title: a.title,
      slug: a.slug,
      href: contentHref('article', a.slug),
      coverImageUrl: a.coverImageUrl,
      coverImageAlt: a.coverImageAlt,
      percent: a.scrollPercent,
      lastActivityAt: a.lastReadAt,
    });
  }
  for (const v of args.videos) {
    if (v.durationSeconds == null || v.durationSeconds <= 0) continue;
    const percent = Math.min(
      100,
      Math.max(0, Math.round((v.positionSeconds / v.durationSeconds) * 100)),
    );
    if (percent < CONTINUE_BAND_MIN || percent >= CONTINUE_BAND_MAX) continue;
    merged.push({
      contentId: v.contentId,
      kind: 'video',
      title: v.title,
      slug: v.slug,
      href: contentHref('video', v.slug),
      coverImageUrl: v.coverImageUrl,
      coverImageAlt: v.coverImageAlt,
      percent,
      lastActivityAt: v.lastWatchedAt,
    });
  }

  merged.sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt));
  const out: ContinueItem[] = [];
  const seen = new Set<string>();
  for (const item of merged) {
    if (seen.has(item.contentId)) continue;
    seen.add(item.contentId);
    out.push(item);
    if (out.length >= limit) break;
  }
  return out;
}

export const homeQueries = {
  // The unified resume surface (Spec 35): in-progress articles (reading_progress)
  // and videos (watch_progress), merged most-recent-first into one "Continue"
  // row. Both own-row RLS, both aged out at 60 days. Videos resume at their saved
  // position via the watch route (Spec 33).
  continueItems: (userId: string | undefined) =>
    queryOptions({
      queryKey: ['home', 'continue', userId] as const,
      queryFn: async (): Promise<ContinueItem[]> => {
        if (!userId) return [];
        const cutoff = continueWatchingCutoffIso(new Date());
        const [articlesRes, videosRes] = await Promise.all([
          supabase
            .from('reading_progress')
            .select(
              'content_id, scroll_percent, last_read_at, content:content_id (title, slug, type, cover_image_url, cover_image_alt)',
            )
            .eq('user_id', userId)
            .eq('completed', false)
            .gte('scroll_percent', 5)
            .lt('scroll_percent', 95)
            .gt('last_read_at', cutoff)
            .order('last_read_at', { ascending: false })
            .limit(8),
          supabase
            .from('watch_progress')
            .select(
              'content_id, position_seconds, duration_seconds, last_watched_at, content:content_id!inner (title, slug, cover_image_url, cover_image_alt, status, type)',
            )
            .eq('user_id', userId)
            .eq('completed', false)
            .gt('position_seconds', 0)
            .gt('last_watched_at', cutoff)
            .eq('content.status', 'published')
            .eq('content.type', 'video')
            .order('last_watched_at', { ascending: false })
            .limit(8),
        ]);
        if (articlesRes.error) throw articlesRes.error;
        if (videosRes.error) throw videosRes.error;

        const articles: ContinueArticleInput[] = (articlesRes.data ?? [])
          .filter((r) => (r.content as { type?: string } | null)?.type === 'article')
          .map((r) => {
            const c = r.content as {
              title: string;
              slug: string;
              cover_image_url: string | null;
              cover_image_alt: string | null;
            };
            return {
              contentId: r.content_id,
              title: c.title,
              slug: c.slug,
              coverImageUrl: c.cover_image_url,
              coverImageAlt: c.cover_image_alt,
              scrollPercent: r.scroll_percent,
              lastReadAt: r.last_read_at,
            };
          });

        const videos: ContinueVideoInput[] = (videosRes.data ?? []).map((r) => {
          const c = r.content as unknown as {
            title: string;
            slug: string;
            cover_image_url: string | null;
            cover_image_alt: string | null;
          };
          return {
            contentId: r.content_id,
            title: c.title,
            slug: c.slug,
            coverImageUrl: c.cover_image_url,
            coverImageAlt: c.cover_image_alt,
            positionSeconds: r.position_seconds,
            durationSeconds: r.duration_seconds,
            lastWatchedAt: r.last_watched_at,
          };
        });

        return buildContinueItems({ articles, videos });
      },
      enabled: Boolean(userId),
    }),

  newForYouCandidates: () =>
    queryOptions({
      queryKey: ['home', 'candidates'] as const,
      queryFn: async (): Promise<HomeCandidate[]> => {
        const { data, error } = await supabase
          .from('content')
          .select(
            'id, type, title, slug, tradition_tags, is_editors_pick, published_at, creator:profiles!content_creator_id_fkey(display_name, username, avatar_url)',
          )
          .eq('status', 'published')
          .order('published_at', { ascending: false })
          .limit(24);
        if (error) throw error;
        return (data ?? []).map((c) => ({
          contentId: c.id,
          type: c.type,
          title: c.title,
          slug: c.slug,
          traditionTags: c.tradition_tags ?? [],
          isEditorsPick: c.is_editors_pick,
          publishedAt: c.published_at,
          creatorName: c.creator?.display_name ?? c.creator?.username ?? null,
          creatorAvatarUrl: c.creator?.avatar_url ?? null,
        }));
      },
    }),

  // Shared subscriber_preferences read: preferred_traditions (home cascade) +
  // email_digest_frequency (the Notifications settings control).
  preferences: (userId: string | undefined) =>
    queryOptions({
      queryKey: ['home', 'preferences', userId] as const,
      queryFn: async (): Promise<{
        preferred_traditions: string[];
        email_digest_frequency: string | null;
      } | null> => {
        if (!userId) return null;
        const { data, error } = await supabase
          .from('subscriber_preferences')
          .select('preferred_traditions, email_digest_frequency')
          .eq('user_id', userId)
          .maybeSingle();
        if (error) throw error;
        return data ?? null;
      },
      enabled: Boolean(userId),
    }),
};

/** Persist the email-digest choice (upsert, mirroring onboarding). */
export async function setDigestFrequency(
  userId: string,
  frequency: 'weekly' | 'monthly' | 'never',
): Promise<void> {
  const { error } = await supabase
    .from('subscriber_preferences')
    .upsert({ user_id: userId, email_digest_frequency: frequency }, { onConflict: 'user_id' });
  if (error) throw error;
}
